import { useState, useMemo, useRef } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { FolderTreePicker } from '@/components/FolderTreePicker'
import { useMediaFolders } from '@/api/hooks/useMediaLibrary'
import { useMediaUsage } from '@/api/hooks/usePoster'
import { useYouTubeChannels } from '@/api/hooks/useYouTube'
import { FolderOpen, Search, Tag, ChevronDown, Youtube, Instagram, Facebook, Check, Trash2, GripVertical } from 'lucide-react'
import type { Account } from '@/types'
import type { WizardState, WizardAction, PostItem } from './types'

interface Props {
  state: WizardState
  dispatch: React.Dispatch<WizardAction>
  accounts: Account[]
}

export function WizardStep1({ state, dispatch, accounts }: Props) {
  const [folderPickerOpen, setFolderPickerOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [tagFilter, setTagFilter] = useState('all')
  const [dragIdx, setDragIdx] = useState<number | null>(null)

  const { data: mediaFolders } = useMediaFolders()
  const { data: ytChannels } = useYouTubeChannels()
  const selectedFolder = mediaFolders?.find(f => f.id === state.selectedFolderId)

  const allUrls = useMemo(() => {
    const urls = new Set(state.items.map(i => i.url).filter(Boolean))
    if (selectedFolder) selectedFolder.images.forEach(img => urls.add(img.url))
    return Array.from(urls)
  }, [state.items, selectedFolder])
  const { data: mediaUsageData } = useMediaUsage(allUrls)

  const allTags = useMemo(() => {
    const tags = new Set<string>()
    accounts.forEach(a => a.tags?.forEach(t => tags.add(t)))
    return Array.from(tags).sort()
  }, [accounts])

  // Split accounts by platform capability
  const igAccounts = useMemo(() => accounts.filter(a => !a.fbOnly), [accounts])
  const fbAccounts = accounts

  const filteredIG = useMemo(() => igAccounts.filter(a => {
    if (tagFilter !== 'all' && !a.tags?.includes(tagFilter)) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      if (!a.name.toLowerCase().includes(q) && !a.username?.toLowerCase().includes(q)) return false
    }
    return true
  }), [igAccounts, tagFilter, searchQuery])

  const filteredFB = useMemo(() => fbAccounts.filter(a => {
    if (tagFilter !== 'all' && !a.tags?.includes(tagFilter)) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      if (!a.name.toLowerCase().includes(q) && !a.username?.toLowerCase().includes(q)) return false
    }
    return true
  }), [fbAccounts, tagFilter, searchQuery])

  const igSelectedCount = useMemo(() =>
    Object.values(state.selectedAccounts).filter(v => v.ig).length,
  [state.selectedAccounts])

  const fbSelectedCount = useMemo(() =>
    Object.values(state.selectedAccounts).filter(v => v.fb).length,
  [state.selectedAccounts])

  const ytSelectedCount = useMemo(() =>
    Object.values(state.selectedYtChannels).filter(Boolean).length,
  [state.selectedYtChannels])

  const addFromFolder = (img: { id: string; url: string; filename: string; mediaType?: string }) => {
    if (state.items.some(i => i.url === img.url)) return
    dispatch({
      type: 'ADD_ITEMS',
      items: [{
        id: crypto.randomUUID(),
        url: img.url,
        mediaType: (img.mediaType as 'image' | 'video') || 'video',
        filename: img.filename || '',
      }],
    })
  }

  const addAllFromFolder = () => {
    if (!selectedFolder) return
    const existing = new Set(state.items.map(i => i.url))
    const newItems: PostItem[] = selectedFolder.images
      .filter(img => !existing.has(img.url))
      .map(img => ({
        id: crypto.randomUUID(),
        url: img.url,
        mediaType: (img.mediaType as 'image' | 'video') || 'video',
        filename: img.filename || '',
      }))
    if (newItems.length > 0) dispatch({ type: 'ADD_ITEMS', items: newItems })
  }

  const selectAllIG = () => {
    const next = { ...state.selectedAccounts }
    const allSel = filteredIG.every(a => next[a.igUserId]?.ig)
    filteredIG.forEach(a => {
      next[a.igUserId] = { ...(next[a.igUserId] || { ig: false, fb: false }), ig: !allSel }
    })
    dispatch({ type: 'SET_FIELD', field: 'selectedAccounts', value: next })
  }

  const selectAllFB = () => {
    const next = { ...state.selectedAccounts }
    const allSel = filteredFB.every(a => next[a.igUserId]?.fb)
    filteredFB.forEach(a => {
      next[a.igUserId] = { ...(next[a.igUserId] || { ig: false, fb: false }), fb: !allSel }
    })
    dispatch({ type: 'SET_FIELD', field: 'selectedAccounts', value: next })
  }

  const selectAllYT = () => {
    if (!ytChannels) return
    const allSel = ytChannels.every(ch => state.selectedYtChannels[ch.id])
    const next = { ...state.selectedYtChannels }
    ytChannels.forEach(ch => { next[ch.id] = !allSel })
    dispatch({ type: 'SET_FIELD', field: 'selectedYtChannels', value: next })
  }

  // DnD
  const handleDragStart = (idx: number) => setDragIdx(idx)
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault()
    if (dragIdx !== null && dragIdx !== idx) {
      dispatch({ type: 'REORDER_ITEMS', fromIndex: dragIdx, toIndex: idx })
      setDragIdx(idx)
    }
  }
  const handleDragEnd = () => setDragIdx(null)

  const showSearch = accounts.length > 4

  return (
    <div className="grid h-full" style={{ gridTemplateColumns: '1fr 300px' }}>
      {/* LEFT — Library */}
      <div className="border-r border-[rgba(255,255,255,0.09)] p-5 overflow-y-auto">
        {/* Folder picker */}
        <div className="mb-4">
          <button type="button" onClick={() => setFolderPickerOpen(!folderPickerOpen)}
            className={cn('flex h-9 w-full max-w-sm items-center justify-between rounded-lg border px-3 text-xs transition-colors',
              folderPickerOpen ? 'border-[#c9a84c]/40' : 'border-[rgba(255,255,255,0.09)]')}>
            {selectedFolder ? (
              <span className="flex items-center gap-1.5">
                <FolderOpen className="h-3.5 w-3.5 text-[#888580]" />
                <span className="truncate">{selectedFolder.name}</span>
                <span className="text-[#888580]">({selectedFolder.images.length})</span>
              </span>
            ) : (
              <span className="text-[#888580]">Selecionar pasta da biblioteca...</span>
            )}
            <ChevronDown className={cn('h-3.5 w-3.5 text-[#888580] transition-transform', !folderPickerOpen && '-rotate-90')} />
          </button>
          {folderPickerOpen && mediaFolders && (
            <div className="mt-2">
              <FolderTreePicker folders={mediaFolders} selectedId={state.selectedFolderId}
                onSelect={id => { dispatch({ type: 'SET_FIELD', field: 'selectedFolderId', value: id }); setFolderPickerOpen(false) }} />
            </div>
          )}
        </div>

        {/* Folder contents */}
        {selectedFolder && selectedFolder.images.length > 0 && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <Button variant="outline" size="sm" className="text-xs border-[rgba(255,255,255,0.09)]" onClick={addAllFromFolder}>
                Adicionar todos ({selectedFolder.images.length - state.items.filter(i => selectedFolder.images.some(img => img.url === i.url)).length})
              </Button>
            </div>
            <div className="grid grid-cols-5 gap-2 lg:grid-cols-6 xl:grid-cols-8">
              {selectedFolder.images.map(img => {
                const added = state.items.some(i => i.url === img.url)
                const usage = mediaUsageData?.[img.url]
                return (
                  <button key={img.id} type="button" onClick={() => !added && addFromFolder(img)}
                    className={cn('relative aspect-[9/16] overflow-hidden rounded-lg border transition-all',
                      added ? 'opacity-30 border-[rgba(255,255,255,0.09)]' : 'border-[rgba(255,255,255,0.09)] hover:border-[#c9a84c]/30 cursor-pointer')}>
                    {img.mediaType === 'video'
                      ? <video src={img.url} className="h-full w-full object-cover" muted />
                      : <img src={img.url} alt="" className="h-full w-full object-cover" loading="lazy" />}
                    {added && (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#c9a84c]/20">
                        <Check className="h-4 w-4 text-[#c9a84c]" strokeWidth={3} />
                      </div>
                    )}
                    {usage && usage.length > 0 && (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="absolute top-0.5 right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#d29922] text-white shadow" onClick={e => e.stopPropagation()}>
                              <span className="text-[7px] font-bold">!</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[220px] space-y-0.5 text-[10px]">
                            <p className="font-semibold">Ja usada {usage.length}x</p>
                            {usage.slice(0, 3).map((u, j) => (
                              <p key={j}>{u.accountName} - {new Date(u.scheduledAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</p>
                            ))}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-1">
                      <p className="text-[8px] text-white truncate">{img.filename}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          </>
        )}

        {/* Selected items (reorderable) */}
        {state.items.length > 0 && (
          <div className="mt-6">
            <h3 className="text-xs font-semibold text-[#888580] uppercase tracking-wider mb-3">
              Selecionados ({state.items.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {state.items.map((item, idx) => (
                <div key={item.id} draggable onDragStart={() => handleDragStart(idx)}
                  onDragOver={e => handleDragOver(e, idx)} onDragEnd={handleDragEnd}
                  className={cn('group relative w-20 rounded-lg border overflow-hidden transition-all cursor-grab',
                    dragIdx === idx ? 'opacity-50 border-[#c9a84c]' : 'border-[rgba(255,255,255,0.09)]')}>
                  <div className="aspect-[9/16] bg-[#222226]">
                    {item.mediaType === 'video'
                      ? <video src={item.url} className="h-full w-full object-cover" muted />
                      : <img src={item.url} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="p-1">
                    <p className="text-[8px] truncate">{item.filename}</p>
                  </div>
                  <button type="button" onClick={() => dispatch({ type: 'REMOVE_ITEM', id: item.id })}
                    className="absolute top-0.5 right-0.5 hidden group-hover:flex h-4 w-4 items-center justify-center rounded bg-[#dc5046] text-white">
                    <Trash2 className="h-2.5 w-2.5" />
                  </button>
                  <div className="absolute top-0.5 left-0.5 hidden group-hover:flex h-4 w-4 items-center justify-center rounded bg-black/50 text-white">
                    <GripVertical className="h-2.5 w-2.5" />
                  </div>
                  <div className="absolute bottom-7 left-1 bg-black/70 px-1 rounded text-[8px] font-bold text-[#c9a84c]">
                    {idx + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* RIGHT — Destinations */}
      <div className="p-4 overflow-y-auto">
        {showSearch && (
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#888580]" />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Buscar..." className="h-8 pl-8 text-xs bg-transparent border-[rgba(255,255,255,0.09)]" />
            </div>
            {allTags.length > 0 && (
              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger className="h-8 w-28 text-xs"><Tag className="mr-1 h-3 w-3" /><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {allTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {/* Instagram */}
        {igAccounts.length > 0 && (
          <PlatformSection
            icon={<Instagram className="h-3.5 w-3.5" />}
            iconColor="bg-[#c73eb8]/12 text-[#c73eb8]"
            title="Instagram"
            total={filteredIG.length}
            selected={igSelectedCount}
            onSelectAll={selectAllIG}
          >
            <div className="flex flex-wrap gap-1.5">
              {filteredIG.map(a => {
                const isSel = !!state.selectedAccounts[a.igUserId]?.ig
                return (
                  <button key={`ig-${a.igUserId}`} type="button"
                    onClick={() => dispatch({ type: 'TOGGLE_ACCOUNT', igUserId: a.igUserId, platform: 'ig' })}
                    className={cn('flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[10px] transition-all',
                      isSel ? 'border-[#c73eb8] bg-[#c73eb8]/8' : 'border-[rgba(255,255,255,0.09)] hover:border-[rgba(255,255,255,0.15)]')}>
                    <span className={cn('h-1.5 w-1.5 rounded-full', isSel ? 'bg-[#c73eb8]' : 'bg-[rgba(255,255,255,0.09)]')} />
                    <span className="truncate max-w-[100px]">@{a.username}</span>
                  </button>
                )
              })}
            </div>
          </PlatformSection>
        )}

        {/* Facebook */}
        <PlatformSection
          icon={<Facebook className="h-3.5 w-3.5" />}
          iconColor="bg-[#4488ff]/12 text-[#4488ff]"
          title="Facebook"
          total={filteredFB.length}
          selected={fbSelectedCount}
          onSelectAll={selectAllFB}
        >
          <div className="flex flex-wrap gap-1.5">
            {filteredFB.map(a => {
              const isSel = !!state.selectedAccounts[a.igUserId]?.fb
              return (
                <button key={`fb-${a.igUserId}`} type="button"
                  onClick={() => dispatch({ type: 'TOGGLE_ACCOUNT', igUserId: a.igUserId, platform: 'fb' })}
                  className={cn('flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[10px] transition-all',
                    isSel ? 'border-[#4488ff] bg-[#4488ff]/8' : 'border-[rgba(255,255,255,0.09)] hover:border-[rgba(255,255,255,0.15)]')}>
                  <span className={cn('h-1.5 w-1.5 rounded-full', isSel ? 'bg-[#4488ff]' : 'bg-[rgba(255,255,255,0.09)]')} />
                  <span className="truncate max-w-[100px]">{a.name}</span>
                </button>
              )
            })}
          </div>
        </PlatformSection>

        {/* YouTube */}
        {ytChannels && ytChannels.length > 0 && (
          <PlatformSection
            icon={<Youtube className="h-3.5 w-3.5" />}
            iconColor="bg-[#ff4444]/12 text-[#ff4444]"
            title="YouTube"
            total={ytChannels.length}
            selected={ytSelectedCount}
            onSelectAll={selectAllYT}
          >
            <div className="flex flex-wrap gap-1.5">
              {ytChannels.map(ch => {
                const isSel = !!state.selectedYtChannels[ch.id]
                return (
                  <button key={ch.id} type="button"
                    onClick={() => dispatch({ type: 'TOGGLE_YT_CHANNEL', id: ch.id })}
                    className={cn('flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[10px] transition-all',
                      isSel ? 'border-[#ff4444] bg-[#ff4444]/8' : 'border-[rgba(255,255,255,0.09)] hover:border-[rgba(255,255,255,0.15)]')}>
                    <span className={cn('h-1.5 w-1.5 rounded-full', isSel ? 'bg-[#ff4444]' : 'bg-[rgba(255,255,255,0.09)]')} />
                    {ch.channelThumbnail && <img src={ch.channelThumbnail} alt="" className="h-4 w-4 rounded-full" />}
                    <span className="truncate max-w-[100px]">{ch.channelTitle}</span>
                  </button>
                )
              })}
            </div>
          </PlatformSection>
        )}
      </div>
    </div>
  )
}

function PlatformSection({ icon, iconColor, title, total, selected, onSelectAll, children }: {
  icon: React.ReactNode
  iconColor: string
  title: string
  total: number
  selected: number
  onSelectAll: () => void
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(true)
  return (
    <div className="mb-3 border border-[rgba(255,255,255,0.09)] rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(p => !p)}
        className="flex w-full items-center gap-2 px-3 py-2.5 hover:bg-[#222226] transition-colors">
        <div className={cn('flex h-6 w-6 items-center justify-center rounded-md', iconColor)}>{icon}</div>
        <span className="text-xs font-semibold flex-1 text-left">{title}</span>
        <span className={cn('text-[10px] font-semibold', selected > 0 ? 'text-[#c9a84c]' : 'text-[#888580]')}>
          {selected}/{total}
        </span>
        <button type="button" onClick={e => { e.stopPropagation(); onSelectAll() }}
          className="text-[9px] font-semibold text-[#c9a84c] hover:underline">Todos</button>
        <ChevronDown className={cn('h-3 w-3 text-[#888580] transition-transform', !open && '-rotate-90')} />
      </button>
      {open && <div className="px-3 pb-3 pt-1">{children}</div>}
    </div>
  )
}
